/**
 * Growth API client — minimal typed wrapper over the **Peitho** waitlist
 * service `/v1/waitlist/*` HTTP surface (peitho#1 / spec arkheus T5b).
 *
 * Base URL is read at runtime from `runtime-config.api.peitho`. The join
 * endpoint is public — no Authorization header is sent. Errors are
 * surfaced as `GrowthApiError` so the calling component can map status
 * and code to a user-friendly string.
 */
import { getRuntimeConfig } from '@/lib/runtime-config'

export class GrowthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string | null,
    public body: unknown,
  ) {
    super(message)
    this.name = 'GrowthApiError'
  }
}

async function baseUrl(): Promise<string> {
  const cfg = await getRuntimeConfig()
  const url = cfg.api.peitho
  if (!url) throw new Error('runtime-config.api.peitho is not configured')
  return url.replace(/\/$/, '')
}

interface ErrorBody {
  error?: { code?: string; message?: string }
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

function errorCodeOf(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const e = (body as ErrorBody).error
    if (e && typeof e.code === 'string') return e.code
  }
  return null
}

interface JoinWaitlistRequest {
  domain_slug: string
  email: string
  source?: string
  referral_code?: string
  metadata?: Record<string, unknown>
}

interface WaitlistEntry {
  id: string
  domain_slug: string
  email: string
  status: 'pending' | 'invited' | 'converted' | 'declined'
  position?: number
  lite_session_token?: string
  created_at: string
}

interface JoinWaitlistResult {
  entry: WaitlistEntry
  duplicate: boolean
}

interface GrowthReferralCode {
  code: string
  uses?: number | null
  max_uses?: number | null
  expires_at?: string | null
  created_at?: string | null
}

async function bearer(): Promise<string> {
  const { getAccessToken } = await import('@/lib/cerberus/tokens')
  const token = getAccessToken()
  if (!token) throw new GrowthApiError('no access token', 401, null, null)
  return token
}

/**
 * POST /v1/waitlist/entries — public, no auth.
 * Resolves with `{entry, duplicate}` on both 201 (new) and 200 (dedup);
 * rejects with `GrowthApiError` on 4xx/5xx.
 */
export async function joinWaitlist(
  req: JoinWaitlistRequest,
  signal?: AbortSignal,
): Promise<JoinWaitlistResult> {
  const root = await baseUrl()
  const res = await fetch(`${root}/v1/waitlist/entries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    signal,
  })
  const body = await readJson(res)
  if (!res.ok) {
    throw new GrowthApiError(
      `Peitho POST /v1/waitlist/entries -> ${res.status}`,
      res.status,
      errorCodeOf(body),
      body,
    )
  }
  const data = (body as { data: WaitlistEntry; meta?: { duplicate?: boolean } })
  return {
    entry: data.data,
    duplicate: Boolean(data.meta?.duplicate),
  }
}

/**
 * POST /v1/referrals/codes — mint (or fetch the existing) referral code for
 * the authenticated consumer. Cerberus user JWT; self-scoped.
 *
 * Peitho is idempotent here: a user who already has a code gets it back
 * rather than a second one. The consumer growth state (`GET /v1/growth/me`)
 * then reflects the code on its next read — callers should invalidate that
 * query after a successful mint so the Rede Cuidari surface picks up the
 * real `primaryCode`/`referralLink`.
 */
export async function mintReferralCode(
  domainSlug = 'cuidari',
  signal?: AbortSignal,
): Promise<GrowthReferralCode> {
  const root = await baseUrl()
  const token = await bearer()
  const res = await fetch(`${root}/v1/referrals/codes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain_slug: domainSlug }),
    signal,
  })
  const body = await readJson(res)
  if (!res.ok) {
    throw new GrowthApiError(
      `Peitho POST /v1/referrals/codes -> ${res.status}`,
      res.status,
      errorCodeOf(body),
      body,
    )
  }
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: GrowthReferralCode }).data
  }
  return body as GrowthReferralCode
}

/**
 * POST /v1/activity/ping — record that the authenticated user opened the app
 * today. Feeds Peitho's "accessed the app on N distinct days" referral
 * milestone (the `access_days` counter). Self-scoped via the Cerberus user JWT.
 *
 * Best-effort and idempotent on the backend: multiple pings on the same day
 * collapse to one distinct-day. The caller (`useActivityPing`) already
 * debounces to at most one call per local day, so this is a thin transport.
 *
 * `fetchImpl` is injectable so the hook/tests can drive it without a real
 * network (mirrors `checkWaitlistAccess`). Errors throw `GrowthApiError`; the
 * caller swallows them so a failed ping never disturbs the app.
 */
export async function pingActivity(
  domainSlug = 'cuidari',
  opts: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<void> {
  const fetchImpl = opts.fetchImpl ?? fetch
  const root = await baseUrl()
  const token = await bearer()
  const res = await fetchImpl(`${root}/v1/activity/ping`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain_slug: domainSlug }),
    signal: opts.signal,
  })
  if (!res.ok) {
    const body = await readJson(res)
    throw new GrowthApiError(
      `Peitho POST /v1/activity/ping -> ${res.status}`,
      res.status,
      errorCodeOf(body),
      body,
    )
  }
}

/**
 * GET /v1/waitlist/entries/{id}/email?token=...
 *
 * Used by the /entrar invite-redemption page to pre-fill the signup form
 * with the original email without exposing it in the URL.
 */
export async function getEntryEmail(
  entryId: string,
  inviteToken: string,
  signal?: AbortSignal,
): Promise<{ id: string; email: string }> {
  const root = await baseUrl()
  const url = `${root}/v1/waitlist/entries/${encodeURIComponent(entryId)}/email?token=${encodeURIComponent(inviteToken)}`
  const res = await fetch(url, { signal })
  const body = await readJson(res)
  if (!res.ok) {
    throw new GrowthApiError(
      `Peitho GET /v1/waitlist/entries/{id}/email -> ${res.status}`,
      res.status,
      errorCodeOf(body),
      body,
    )
  }
  return (body as { data: { id: string; email: string } }).data
}

/**
 * Explicit LGPD consent captured at signup. Recorded on the waitlist→account
 * conversion so the marketing drip has a lawful basis (and so a user who
 * declines the marketing opt-in is not enrolled in `cuidari_waitlist_nurture`
 * follow-ups). `terms` is the mandatory acceptance of the terms of use; it is
 * only ever `true` here (the form blocks submit otherwise) but is sent so the
 * record is self-describing.
 */
interface SignupConsent {
  /** User ticked the marketing-email opt-in. */
  marketing: boolean
  /** User accepted the terms of use / privacy policy. */
  terms: boolean
  /** ISO-8601 timestamp the consent was given (client clock). */
  consented_at: string
}

/**
 * POST /v1/waitlist/entries/{id}/convert — anonymous + invite_token.
 *
 * Marks the entry as converted; called by the /entrar page after the
 * user has finished the Cerberus signup successfully.
 *
 * `consent` (optional) records the explicit LGPD choices the user made on the
 * signup form. It rides the conversion call as `metadata.consent` — an
 * additive field a backend that doesn't read it ignores, so this is safe to
 * ship ahead of the Peitho-side persistence.
 */
export async function convertEntry(
  entryId: string,
  inviteToken: string,
  cerberusUserId: string,
  consent?: SignupConsent,
  signal?: AbortSignal,
): Promise<void> {
  const root = await baseUrl()
  const res = await fetch(
    `${root}/v1/waitlist/entries/${encodeURIComponent(entryId)}/convert`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invite_token: inviteToken,
        cerberus_user_id: cerberusUserId,
        ...(consent ? { metadata: { consent } } : {}),
      }),
      signal,
    },
  )
  if (!res.ok) {
    const body = await readJson(res)
    throw new GrowthApiError(
      `Peitho POST /v1/waitlist/entries/{id}/convert -> ${res.status}`,
      res.status,
      errorCodeOf(body),
      body,
    )
  }
}
